import { ChessTermType, TermAction, } from "@chesshotel/types";
import { Chess, PAWN, WHITE, BLACK, swapColor, } from "@chesshotel/chess";
import { getFen, key2pos, uci2move, pieceValues, pos2key, sortPiecesByMaxValue, exchangeEvaluation, } from "./util.js";
import { isValidFenMove } from "./tactics/util.js";
const chess = new Chess();
function checkForceDoublePawns(fen, move1, move2) {
    if (!move1 || !move2) {
        return null;
    }
    const recapture = isRecapture(fen, move1, move2);
    if (recapture) {
        const newFen = getFen(fen, move1);
        return detectDoublePawns(newFen, move2);
    }
}
function allRecapturesLeadToIsolatedPawn(fen, move1) {
    const chess = new Chess(fen);
    chess.move(uci2move(move1));
    const recaptureSquare = move1.slice(2, 4);
    const color = chess.turn();
    const legalMoves = chess.moves({ verbose: true });
    const recaptureMoves = legalMoves.filter((m) => m.to === recaptureSquare && m.captured);
    if (recaptureMoves.length === 0)
        return false;
    for (const recapture of recaptureMoves) {
        const recaptureUci = (recapture.from +
            recapture.to +
            (recapture.promotion || ""));
        const newFen = getFen(fen, move1);
        const result = detectIsolatedPawn(newFen, recaptureUci);
        if (!result) {
            return false;
        }
    }
    return true;
}
function checkForceIsolatedPawn(fen, move1, move2) {
    if (!move1 || !move2) {
        return null;
    }
    const recapture = isRecapture(fen, move1, move2);
    if (recapture) {
        if (allRecapturesLeadToIsolatedPawn(fen, move1)) {
            const newFen = getFen(fen, move1);
            return detectIsolatedPawn(newFen, move2);
        }
    }
}
function checkOfferTradeConnectPawns(fen, move1, move2, move3) {
    if (!move1 || !move2 || !move3) {
        return null;
    }
    const offerTradeMade = isOfferTradeMade(move1, move2, move3);
    if (offerTradeMade) {
        chess.load(fen);
        chess.move(uci2move(move1));
        const moveResult = chess.move(uci2move(move2));
        if (moveResult) {
            return detectConnectPawns(chess.fen(), move3);
        }
    }
}
function checkOfferTradePassedPawn(fen, move1, move2, move3) {
    if (!move1 || !move2 || !move3) {
        return null;
    }
    const offerTradeMade = isOfferTradeMade(move1, move2, move3);
    if (offerTradeMade) {
        chess.load(fen);
        chess.move(uci2move(move1));
        const moveResult = chess.move(uci2move(move2));
        if (moveResult) {
            return detectPassedPawn(chess.fen(), move3);
        }
    }
}
function isOfferTradeMade(move1, move2, move3) {
    if (!move1 || !move2 || !move3) {
        return false;
    }
    const move1To = move1.slice(2, 4);
    const move2To = move2.slice(2, 4);
    const move3To = move3.slice(2, 4);
    return move1To == move2To && move2To == move3To;
}
function detectDoublePawns(fen, move) {
    const chessBefore = new Chess(fen);
    const color = chessBefore.turn();
    const moveObj = uci2move(move);
    const destFile = key2pos(moveObj.to)[0] - 1;
    const boardBefore = chessBefore.boardPieceColumns();
    let pawnsBefore = 0;
    for (let rank = 0; rank < 8; rank++) {
        const piece = boardBefore[destFile][rank];
        if (piece && piece.type === PAWN && piece.color === color) {
            pawnsBefore++;
        }
    }
    const chessAfter = new Chess(fen);
    const moveResult = chessAfter.move(moveObj);
    if (!moveResult)
        return null;
    if (moveResult.piece !== PAWN ||
        !moveResult.captured ||
        moveResult.promotion) {
        return null;
    }
    const boardAfter = chessAfter.boardPieceColumns();
    let pawnsAfter = 0;
    for (let rank = 0; rank < 8; rank++) {
        const piece = boardAfter[destFile][rank];
        if (piece && piece.type === PAWN && piece.color === color) {
            pawnsAfter++;
        }
    }
    if (pawnsBefore === 1 && pawnsAfter === 2) {
        let isIsolated = false;
        if (destFile === 0) {
            isIsolated = boardAfter[1].every((piece) => !piece || piece.type !== PAWN || piece.color !== color);
        }
        else if (destFile === 7) {
            isIsolated = boardAfter[6].every((piece) => !piece || piece.type !== PAWN || piece.color !== color);
        }
        else {
            isIsolated =
                boardAfter[destFile - 1].every((piece) => !piece || piece.type !== PAWN || piece.color !== color) &&
                    boardAfter[destFile + 1].every((piece) => !piece || piece.type !== PAWN || piece.color !== color);
        }
        return {
            term: ChessTermType.DOUBLE_PAWN,
            isolated: isIsolated,
        };
    }
    return null;
}
export function detectBackwardPawn(fen, move, color = null) {
    const chess = new Chess(fen);
    color = color || chess.turn();
    const moveObj = uci2move(move);
    if (chess.getBoardPiece(moveObj.from).type != PAWN) {
        return null;
    }
    const backwardPawnsBefore = getBackwardPawns(fen, color);
    chess.move(uci2move(move));
    const backwardPawnsAfter = getBackwardPawns(chess.fen(), color);
    if (backwardPawnsAfter.length > backwardPawnsBefore.length) {
        const newBackwardPawn = getNewPieces(backwardPawnsBefore, backwardPawnsAfter);
        if (newBackwardPawn.length > 0) {
            return {
                term: ChessTermType.BACKWARD_PAWN,
                mainPiece: newBackwardPawn[0],
                highlightSquares: newBackwardPawn.map((pawn) => pawn.square),
            };
        }
    }
}
export function getBackwardPawns(fen, color) {
    chess.load(fen);
    const board = chess.boardPieceColumns();
    const backwardPawns = [];
    for (let file = 0; file < 8; file++) {
        const pawns = [];
        pawns[file] = 0;
        for (let rank = 0; rank < 8; rank++) {
            const piece = board[file][rank];
            if (piece && piece.type === PAWN && piece.color === color) {
                const pawnProtected = canBeProtectedByPawns(board, piece.square, color);
                const canAdvance = canAdvancePawn(board, piece.square, color, fen);
                const onEdge = file === 0 || file === 7;
                if (!pawnProtected && !canAdvance && !onEdge) {
                    backwardPawns.push(piece);
                }
            }
        }
    }
    return backwardPawns;
}
function canAdvancePawn(board, square, color, fen) {
    const pos = key2pos(square);
    const col = pos[0];
    const row = pos[1];
    const forward = color === WHITE ? 1 : -1;
    const opponentColor = color === WHITE ? BLACK : WHITE;
    const oneSquareForward = board[col - 1] && board[col - 1][row - 1 + forward];
    const oneSquareForwardKey = pos2key([col, row + forward]);
    const isSafe = isSafeMove(oneSquareForwardKey, color, opponentColor, fen);
    if (!oneSquareForward && isSafe) {
        return true;
    }
    const startingRank = color === WHITE ? 2 : 7;
    if (row === startingRank) {
        const twoSquaresForward = board[col - 1] && board[col - 1][row - 1 + 2 * forward];
        const twoSquaresForwardKey = pos2key([col, row + forward * 2]);
        if (!twoSquaresForward &&
            !oneSquareForward &&
            isSafeMove(twoSquaresForwardKey, color, opponentColor, fen)) {
            return true;
        }
    }
    const leftDiagonal = board[row + forward] && board[row + forward][col - 1];
    const rightDiagonal = board[row + forward] && board[row + forward][col + 1];
    if ((leftDiagonal && leftDiagonal.color !== color) ||
        (rightDiagonal && rightDiagonal.color !== color)) {
        return true;
    }
    return false;
}
function isSafeMove(square, color, opponentColor, fen) {
    const chess = new Chess(fen);
    const attackers = chess.attackers(square, opponentColor).map((square) => {
        const piece = chess.get(square);
        return { ...piece, square };
    });
    const defenders = chess.attackers(square, color).map((square) => {
        const piece = chess.get(square);
        return { ...piece, square };
    });
    if (attackers.length === 0) {
        return true;
    }
    if (defenders.length === 0) {
        return false;
    }
    const sortedAttackers = sortPiecesByMaxValue(attackers);
    const sortedDefenders = sortPiecesByMaxValue(defenders);
    for (let i = 0; i < sortedAttackers.length || i < sortedDefenders.length; i++) {
        const attacker = sortedAttackers[i];
        const defender = sortedDefenders[i];
        if (attacker && !defender) {
            return false;
        }
        if (defender && !attacker) {
            return true;
        }
        if (attacker && defender) {
            if (pieceValues[attacker.type] < pieceValues[defender.type]) {
                return false;
            }
        }
    }
    return true;
}
function canBeProtectedByPawns(board, square, color) {
    const file = key2pos(square)[0] - 1;
    const rank = key2pos(square)[1] - 1;
    const rightPawn = canPawnProtect(board, file, rank, color, true);
    const leftPawn = canPawnProtect(board, file, rank, color, false);
    return rightPawn || leftPawn;
}
function getFirstPawnOnFile(board, file, color) {
    const rows = board[file];
    const pawns = rows.filter((piece) => piece && piece.type === PAWN && piece.color === color);
    const firstPawn = color === WHITE ? pawns[0] : pawns[pawns.length - 1];
    return firstPawn;
}
function canPawnProtect(board, file, rank, color, isRight) {
    const nextPawnFile = isRight ? file + 1 : file - 1;
    const nextOutside = nextPawnFile < 0 || nextPawnFile > 7;
    if (nextOutside) {
        return false;
    }
    const nextPawn = getFirstPawnOnFile(board, nextPawnFile, color);
    if (nextPawn) {
        const nextPawnRank = key2pos(nextPawn.square)[1] - 1;
        const isBehind = color === WHITE ? nextPawnRank <= rank : nextPawnRank >= rank;
        return isBehind;
    }
    return false;
}
export function detectPassedPawn(fen, move, color = null) {
    const chess = new Chess(fen);
    color = color || chess.turn();
    const passedPawnsBefore = getPassedPawns(chess, color);
    chess.move(uci2move(move));
    const passedPawnsAfter = getPassedPawns(chess, color);
    if (passedPawnsAfter.length > passedPawnsBefore.length) {
        const evalResult = exchangeEvaluation(chess.fen(), passedPawnsAfter[0].square, { backtrackBadMoves: true });
        if (evalResult.gain > 0 ||
            (evalResult.gain == 0 && evalResult.moveSequence.length > 1)) {
            return null;
        }
        const newPassedPawn = getNewPieces(passedPawnsBefore, passedPawnsAfter);
        if (newPassedPawn.length > 0) {
            return {
                term: ChessTermType.PASSED_PAWN,
                mainPiece: newPassedPawn[0],
                translationKeys: {
                    passedPawn: newPassedPawn.length > 1 ? "passed_pawns" : "a_passed_pawn",
                },
                highlightSquares: newPassedPawn.map((pawn) => pawn.square),
            };
        }
    }
}
function getPassedPawns(chess, color) {
    const board = chess.boardPieceColumns();
    const opponentColor = swapColor(color);
    const passedPawns = [];
    for (let file = 0; file < 8; file++) {
        const pawns = [];
        pawns[file] = 0;
        for (let rank = 0; rank < 8; rank++) {
            const piece = board[file][rank];
            if (piece && piece.type === PAWN && piece.color === color) {
                const isBlocking = checkOpponentPawnsBlocks(board, file, rank, opponentColor);
                if (!isBlocking) {
                    passedPawns.push(piece);
                }
            }
        }
    }
    return passedPawns;
}
function checkOpponentPawnsBlocks(board, file, rank, opponentColor) {
    for (let i = -1; i < 2; i++) {
        const currFile = file + i;
        if (currFile < 0 || currFile > 7)
            continue;
        const pawn = getFirstPawnOnFile(board, currFile, opponentColor);
        if (pawn) {
            const pawnRank = key2pos(pawn.square)[1] - 1;
            const isInfront = opponentColor === WHITE ? pawnRank < rank : pawnRank > rank;
            if (isInfront) {
                return true;
            }
        }
    }
    return false;
}
function getNewPieces(prevPieces, pieces) {
    return pieces.filter((piece) => !prevPieces.some((p) => p.square === piece.square &&
        p.color === piece.color &&
        p.type === piece.type));
}
export function getIsolatedPawns(fen, color) {
    const chess = new Chess(fen);
    const board = chess.boardPieceColumns();
    const isolatedPawns = [];
    const fileHasPawn = [];
    for (let file = 0; file < 8; file++) {
        const pawn = getFirstPawnOnFile(board, file, color);
        fileHasPawn[file] = pawn;
    }
    for (let file = 0; file < 8; file++) {
        if (file == 0) {
            const pawn = fileHasPawn[file];
            const nextPawn = fileHasPawn[file + 1];
            if (pawn && !nextPawn) {
                isolatedPawns.push(pawn);
            }
        }
        if (file == 7) {
            const pawn = fileHasPawn[file];
            const nextPawn = fileHasPawn[file - 1];
            if (pawn && !nextPawn) {
                isolatedPawns.push(pawn);
            }
        }
        if (file != 0 && file != 7) {
            const prevPawn = fileHasPawn[file - 1];
            const pawn = fileHasPawn[file];
            const nextPawn = fileHasPawn[file + 1];
            if (pawn && !nextPawn && !prevPawn) {
                isolatedPawns.push(pawn);
            }
        }
    }
    return isolatedPawns;
}
export function detectIsolatedPawn(fen, move) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveResult = chess.move(uci2move(move));
    if (!moveResult)
        return null;
    const nextFen = chess.fen();
    const prevIsolatedPawns = getIsolatedPawns(fen, color);
    const isolatedPawns = getIsolatedPawns(nextFen, color);
    if (prevIsolatedPawns.length < isolatedPawns.length) {
        const newIsolatedPawns = getNewPieces(prevIsolatedPawns, isolatedPawns);
        if (newIsolatedPawns.length > 0) {
            const translationKeys = {
                color: opponentColor,
            };
            return {
                term: ChessTermType.ISOLATED_PAWN,
                mainPiece: newIsolatedPawns[0],
                highlightSquares: newIsolatedPawns.map((pawn) => pawn.square),
                termAction: TermAction.PERFORM,
                translationKeys,
            };
        }
    }
}
export function detectConnectPawns(fen, move) {
    chess.load(fen);
    const color = chess.turn();
    const moveResult = chess.move(uci2move(move));
    const newFen = chess.fen();
    if (!moveResult || moveResult.promotion)
        return null;
    const prevIsolatedPawns = getIsolatedPawns(fen, color);
    const isolatedPawns = getIsolatedPawns(newFen, color);
    if (prevIsolatedPawns.length > isolatedPawns.length) {
        const connectedPawns = getNewPieces(isolatedPawns, prevIsolatedPawns);
        if (connectedPawns.length > 0) {
            return {
                term: ChessTermType.CONNECT_PAWN,
                mainPiece: connectedPawns[0],
                highlightSquares: connectedPawns.map((pawn) => pawn.square),
            };
        }
    }
}
function detectPromotion(fen, move) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(move));
    if (moveResult.promotion) {
        return {
            term: ChessTermType.PROMOTION,
            move: moveResult,
            highlightSquares: [moveResult.to],
            translationKeys: {
                piece: moveResult.promotion,
            },
        };
    }
}
function isRecapture(fen, move1, move2) {
    chess.load(fen);
    const moveResult = chess.move(uci2move(move1));
    const lastMoveTo = move1.slice(2, 4);
    const moveTo = move2.slice(2, 4);
    return moveResult.captured && lastMoveTo == moveTo;
}
export function detectPerformDoublePawn(position, lastPosition) {
    const doublePawn = detectDoublePawns(lastPosition.fen, position.move);
    if (doublePawn) {
        return {
            ...doublePawn,
            term: ChessTermType.DOUBLE_PAWN,
            termAction: TermAction.PERFORM,
        };
    }
}
export function detectForceDoublePawn(position, lastPosition, termAction) {
    let doublePawn;
    const { move, bestMove } = position;
    if (termAction == TermAction.PERFORM) {
        doublePawn = checkForceDoublePawns(lastPosition.fen, move, bestMove);
    }
    if (termAction == TermAction.ALLOW) {
        const { fen, pvMoves } = position;
        const user = checkForceDoublePawns(fen, pvMoves[0], pvMoves[1]);
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const best = checkForceDoublePawns(bestFen, lastPosition.pvMoves[1], lastPosition.pvMoves[2]);
        if (user && !best) {
            doublePawn = user;
        }
    }
    if (termAction == TermAction.MISS) {
        const { fen, pvMoves } = lastPosition;
        const { move, bestMove } = position;
        const userDoublePawn = checkForceDoublePawns(fen, move, bestMove);
        const bestDoublePawn = checkForceDoublePawns(fen, pvMoves[0], pvMoves[1]);
        if (!userDoublePawn && bestDoublePawn) {
            doublePawn = bestDoublePawn;
        }
    }
    if (doublePawn) {
        return {
            ...doublePawn,
            term: ChessTermType.FORCE_DOUBLE_PAWN,
            termAction,
        };
    }
}
export function detectForceIsolatedPawn(position, lastPosition, termAction) {
    let isolatedPawn;
    if (termAction == TermAction.PERFORM) {
        const { move, bestMove } = position;
        isolatedPawn = checkForceIsolatedPawn(lastPosition.fen, move, bestMove);
    }
    if (termAction == TermAction.ALLOW) {
        const { fen, pvMoves } = position;
        const user = checkForceIsolatedPawn(fen, pvMoves[0], pvMoves[1]);
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const best = checkForceIsolatedPawn(bestFen, lastPosition.pvMoves[1], lastPosition.pvMoves[2]);
        if (user && !best) {
            isolatedPawn = user;
        }
    }
    if (termAction == TermAction.MISS) {
        const { fen, pvMoves } = lastPosition;
        const { move, bestMove } = position;
        const userIsolatedPawn = checkForceIsolatedPawn(fen, move, bestMove);
        const bestIsolatedPawn = checkForceIsolatedPawn(fen, pvMoves[0], pvMoves[1]);
        if (!userIsolatedPawn && bestIsolatedPawn) {
            isolatedPawn = bestIsolatedPawn;
        }
    }
    if (isolatedPawn) {
        return {
            ...isolatedPawn,
            term: ChessTermType.FORCE_ISOLATE_PAWN,
            termAction,
        };
    }
}
export function detectOfferTradeConnectPawns(position, lastPosition, termAction) {
    let connectPawn;
    if (termAction == TermAction.PERFORM) {
        const { move, pvMoves } = position;
        connectPawn = checkOfferTradeConnectPawns(lastPosition.fen, move, pvMoves[0], pvMoves[1]);
    }
    if (termAction == TermAction.ALLOW) {
        const { fen, pvMoves } = position;
        const user = checkOfferTradeConnectPawns(fen, pvMoves[0], pvMoves[1], pvMoves[2]);
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const best = checkOfferTradeConnectPawns(bestFen, lastPosition.pvMoves[1], lastPosition.pvMoves[2], lastPosition.pvMoves[3]);
        if (user && !best) {
            connectPawn = user;
        }
    }
    if (termAction == TermAction.MISS) {
        const { fen, pvMoves } = lastPosition;
        const { move, bestMove } = position;
        const userIsolatedPawn = checkOfferTradeConnectPawns(fen, move, bestMove, position.pvMoves[1]);
        const bestIsolatedPawn = checkOfferTradeConnectPawns(fen, pvMoves[0], pvMoves[1], pvMoves[2]);
        if (!userIsolatedPawn && bestIsolatedPawn) {
            connectPawn = bestIsolatedPawn;
        }
    }
    if (connectPawn) {
        return {
            ...connectPawn,
            term: ChessTermType.OFFER_TRADE_CONNECT_PAWN,
            termAction,
        };
    }
}
export function detectOfferTradePassedPawn(position, lastPosition, termAction) {
    let passedPawn;
    if (termAction == TermAction.PERFORM) {
        const { move, pvMoves } = position;
        passedPawn = checkOfferTradePassedPawn(lastPosition.fen, move, pvMoves[0], pvMoves[1]);
    }
    if (termAction == TermAction.ALLOW) {
        const { fen, pvMoves } = position;
        const user = checkOfferTradePassedPawn(fen, pvMoves[0], pvMoves[1], pvMoves[2]);
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const best = checkOfferTradePassedPawn(bestFen, lastPosition.pvMoves[1], lastPosition.pvMoves[2], lastPosition.pvMoves[3]);
        if (user && !best) {
            passedPawn = user;
        }
    }
    if (termAction == TermAction.MISS) {
        const { fen, pvMoves } = lastPosition;
        const { move, bestMove } = position;
        const user = checkOfferTradePassedPawn(fen, move, bestMove, position.pvMoves[1]);
        const best = checkOfferTradePassedPawn(fen, pvMoves[0], pvMoves[1], pvMoves[2]);
        if (!user && best) {
            passedPawn = best;
        }
    }
    if (passedPawn) {
        return {
            ...passedPawn,
            term: ChessTermType.OFFER_TRADE_PASSED_PAWN,
            termAction,
        };
    }
}
export function detectPassedPawnActions(position, lastPosition, termAction) {
    if (termAction == TermAction.PERFORM) {
        const passedPawn = detectPassedPawn(lastPosition.fen, position.move);
        if (passedPawn) {
            return { ...passedPawn, termAction };
        }
    }
    if (termAction == TermAction.CREATE) {
        const opponentColor = position.moveIndex % 2 == 0 ? WHITE : BLACK;
        const passedPawn = detectPassedPawn(lastPosition.fen, position.move, opponentColor);
        if (passedPawn) {
            return { ...passedPawn, termAction };
        }
    }
    if (termAction == TermAction.ALLOW) {
        const opponentMove = position.pvMoves[0];
        const user = detectPassedPawn(position.fen, opponentMove);
        if (user) {
            const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
            const isValid = isValidFenMove(bestFen, opponentMove);
            const best = isValid && detectPassedPawn(bestFen, opponentMove);
            if (!best) {
                return { ...user, termAction };
            }
        }
    }
    if (termAction == TermAction.MISS) {
        const user = detectPassedPawn(lastPosition.fen, position.move);
        const best = detectPassedPawn(lastPosition.fen, lastPosition.bestMove);
        if (best && !user) {
            return { ...best, termAction };
        }
    }
}
export function detectPromotionActions(position, lastPosition, termAction) {
    if (termAction == TermAction.PERFORM) {
        const promotion = detectPromotion(lastPosition.fen, position.move);
        if (promotion) {
            return { ...promotion, termAction };
        }
    }
    if (termAction == TermAction.ALLOW) {
        const opponentMove = position.pvMoves[0];
        const user = detectPromotion(position.fen, opponentMove);
        if (user) {
            const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
            const isValid = isValidFenMove(bestFen, opponentMove);
            const best = isValid && detectPromotion(bestFen, opponentMove);
            if (!best) {
                return { ...user, termAction };
            }
        }
    }
    if (termAction == TermAction.MISS) {
        const user = detectPromotion(lastPosition.fen, position.move);
        const best = detectPromotion(lastPosition.fen, lastPosition.bestMove);
        if (best && !user) {
            return { ...best, termAction };
        }
    }
    if (termAction == TermAction.BETTER_WAY) {
        const user = detectPromotion(lastPosition.fen, position.move);
        const best = detectPromotion(lastPosition.fen, lastPosition.bestMove);
        if (best && user) {
            return { ...best, termAction };
        }
    }
}
export function detectConnectPawnActions(position, lastPosition, termAction) {
    if (termAction == TermAction.PERFORM) {
        const connectPawns = detectConnectPawns(lastPosition.fen, position.move);
        if (connectPawns) {
            return { ...connectPawns, termAction: TermAction.PERFORM };
        }
    }
    if (termAction == TermAction.ALLOW) {
        const opponentMove = position.pvMoves[0];
        const user = detectConnectPawns(position.fen, opponentMove);
        if (user) {
            const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
            const isValid = isValidFenMove(bestFen, opponentMove);
            const best = isValid && detectConnectPawns(bestFen, opponentMove);
            if (!best) {
                return { ...user, termAction: TermAction.ALLOW };
            }
        }
    }
    if (termAction == TermAction.MISS) {
        const user = detectConnectPawns(lastPosition.fen, position.move);
        const best = detectConnectPawns(lastPosition.fen, lastPosition.bestMove);
        if (best && !user) {
            return { ...best, termAction: TermAction.MISS };
        }
    }
}
export function detectBackwardPawnActions(position, lastPosition, termAction) {
    if (termAction == TermAction.PERFORM) {
        const backwardPawn = detectBackwardPawn(lastPosition.fen, position.move);
        if (backwardPawn) {
            return { ...backwardPawn, termAction };
        }
    }
    if (termAction == TermAction.CREATE) {
        const opponentColor = position.moveIndex % 2 == 0 ? WHITE : BLACK;
        const backwardPawn = detectBackwardPawn(lastPosition.fen, position.move, opponentColor);
        if (backwardPawn) {
            return { ...backwardPawn, termAction };
        }
    }
}
function detectCreatePawnChain(fen, move, moveIndex) {
    chess.load(fen);
    const color = chess.turn();
    const moveResult = chess.move(uci2move(move));
    if (!moveResult || moveResult.piece != PAWN) {
        return null;
    }
    if (moveIndex < 12) {
        const fromPos = key2pos(moveResult.from);
        const toPos = key2pos(moveResult.to);
        if ((color == WHITE && fromPos[1] == 2 && toPos[1] == 3) ||
            (color == BLACK && fromPos[1] == 7 && toPos[1] == 6)) {
            return null;
        }
    }
    const pawnPos = key2pos(moveResult.to);
    const pawnFile = pawnPos[0];
    const pawnRank = pawnPos[1];
    let leftPawn = null;
    let rightPawn = null;
    if (pawnFile > 1) {
        const rankAhead = color === WHITE ? pawnRank + 1 : pawnRank - 1;
        const rankBehind = color === WHITE ? pawnRank - 1 : pawnRank + 1;
        const leftPawnSquareAhead = pos2key([pawnFile - 1, rankAhead]);
        const leftPawnSquareBehind = pos2key([pawnFile - 1, rankBehind]);
        const pieceAhead = chess.get(leftPawnSquareAhead);
        if (pieceAhead && pieceAhead.color === color && pieceAhead.type === PAWN) {
            leftPawn = pieceAhead;
        }
        const pieceBehind = chess.get(leftPawnSquareBehind);
        if (pieceBehind &&
            pieceBehind.color === color &&
            pieceBehind.type === PAWN) {
            leftPawn = pieceBehind;
        }
    }
    if (pawnFile < 8) {
        const rankAhead = color === WHITE ? pawnRank + 1 : pawnRank - 1;
        const rankBehind = color === WHITE ? pawnRank - 1 : pawnRank + 1;
        const rightPawnSquareAhead = pos2key([pawnFile + 1, rankAhead]);
        const rightPawnSquareBehind = pos2key([pawnFile + 1, rankBehind]);
        const pieceAhead = chess.get(rightPawnSquareAhead);
        if (pieceAhead && pieceAhead.color === color && pieceAhead.type === PAWN) {
            leftPawn = pieceAhead;
        }
        const pieceBehind = chess.get(rightPawnSquareBehind);
        if (pieceBehind &&
            pieceBehind.color === color &&
            pieceBehind.type === PAWN) {
            leftPawn = pieceBehind;
        }
    }
    if (leftPawn || rightPawn) {
        return {
            term: ChessTermType.PAWN_CHAIN,
            move: moveResult,
        };
    }
}
export function detectPawnChainActions(position, lastPosition, termAction) {
    if (termAction == TermAction.PERFORM) {
        const user = detectCreatePawnChain(lastPosition.fen, position.move, position.moveIndex);
        if (user) {
            return { ...user, termAction: TermAction.PERFORM };
        }
    }
    if (termAction == TermAction.ALLOW) {
        const opponentMove = position.pvMoves[0];
        const user = detectCreatePawnChain(position.fen, opponentMove, position.moveIndex);
        if (user) {
            const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
            const isValid = isValidFenMove(bestFen, opponentMove);
            const best = isValid &&
                detectCreatePawnChain(bestFen, opponentMove, position.moveIndex);
            if (!best) {
                return { ...user, termAction: TermAction.ALLOW };
            }
        }
    }
    if (termAction == TermAction.MISS) {
        const user = detectCreatePawnChain(lastPosition.fen, position.move, position.moveIndex);
        const best = detectCreatePawnChain(lastPosition.fen, lastPosition.bestMove, position.moveIndex);
        if (best && !user) {
            return { ...best, termAction: TermAction.MISS };
        }
    }
    if (termAction == TermAction.BETTER_WAY) {
        const user = detectCreatePawnChain(lastPosition.fen, position.move, position.moveIndex);
        const best = detectCreatePawnChain(lastPosition.fen, lastPosition.bestMove, position.moveIndex);
        if (best && user && position.move !== lastPosition.bestMove) {
            return { ...best, termAction: TermAction.BETTER_WAY };
        }
    }
}
export function negativePawnPlacements(position, lastPosition) {
    const negativeTerms = [];
    const missForceDoublePawn = detectForceDoublePawn(position, lastPosition, TermAction.MISS);
    const allowForceDoublePawn = detectForceDoublePawn(position, lastPosition, TermAction.ALLOW);
    const missForceIsolatedPawn = detectForceIsolatedPawn(position, lastPosition, TermAction.MISS);
    const allowForceIsolatedPawn = detectForceIsolatedPawn(position, lastPosition, TermAction.ALLOW);
    if (missForceDoublePawn) {
        negativeTerms.push(missForceDoublePawn);
    }
    if (allowForceDoublePawn) {
        negativeTerms.push(allowForceDoublePawn);
    }
    if (missForceIsolatedPawn) {
        negativeTerms.push(missForceIsolatedPawn);
    }
    if (allowForceIsolatedPawn) {
        negativeTerms.push(allowForceIsolatedPawn);
    }
    const missOfferTradeConnectPawns = detectOfferTradeConnectPawns(position, lastPosition, TermAction.MISS);
    const allowOfferTradeConnectPawns = detectOfferTradeConnectPawns(position, lastPosition, TermAction.ALLOW);
    const missOfferTradePassedPawn = detectOfferTradePassedPawn(position, lastPosition, TermAction.MISS);
    const allowOfferTradePassedPawn = detectOfferTradePassedPawn(position, lastPosition, TermAction.ALLOW);
    if (missOfferTradeConnectPawns) {
        negativeTerms.push(missOfferTradeConnectPawns);
    }
    if (allowOfferTradeConnectPawns) {
        negativeTerms.push(allowOfferTradeConnectPawns);
    }
    if (missOfferTradePassedPawn) {
        negativeTerms.push(missOfferTradePassedPawn);
    }
    if (allowOfferTradePassedPawn) {
        negativeTerms.push(allowOfferTradePassedPawn);
    }
    const missPassedPawn = detectPassedPawnActions(position, lastPosition, TermAction.MISS);
    const allowPassedPawn = detectPassedPawnActions(position, lastPosition, TermAction.ALLOW);
    const createPassedPawn = detectPassedPawnActions(position, lastPosition, TermAction.CREATE);
    if (missPassedPawn) {
        negativeTerms.push(missPassedPawn);
    }
    if (allowPassedPawn) {
        negativeTerms.push(allowPassedPawn);
    }
    if (createPassedPawn) {
        negativeTerms.push(createPassedPawn);
    }
    const missPromotion = detectPromotionActions(position, lastPosition, TermAction.MISS);
    const allowPromotion = detectPromotionActions(position, lastPosition, TermAction.ALLOW);
    const betterWayPromotion = detectPromotionActions(position, lastPosition, TermAction.BETTER_WAY);
    if (missPromotion) {
        negativeTerms.push(missPromotion);
    }
    if (allowPromotion) {
        negativeTerms.push(allowPromotion);
    }
    if (betterWayPromotion) {
        negativeTerms.push(betterWayPromotion);
    }
    const missConnectPawn = detectConnectPawnActions(position, lastPosition, TermAction.MISS);
    const allowConnectPawn = detectConnectPawnActions(position, lastPosition, TermAction.ALLOW);
    const createConnectPawn = detectConnectPawnActions(position, lastPosition, TermAction.CREATE);
    if (missConnectPawn) {
        negativeTerms.push(missConnectPawn);
    }
    if (allowConnectPawn) {
        negativeTerms.push(allowConnectPawn);
    }
    if (createConnectPawn) {
        negativeTerms.push(createConnectPawn);
    }
    const performBackwardPawn = detectBackwardPawnActions(position, lastPosition, TermAction.PERFORM);
    if (performBackwardPawn) {
        negativeTerms.push(performBackwardPawn);
    }
    const missCreatePawnChain = detectPawnChainActions(position, lastPosition, TermAction.MISS);
    if (missCreatePawnChain) {
        negativeTerms.push(missCreatePawnChain);
    }
    const allowCreatePawnChain = detectPawnChainActions(position, lastPosition, TermAction.ALLOW);
    if (allowCreatePawnChain) {
        negativeTerms.push(allowCreatePawnChain);
    }
    const betterWayCreatePawnChain = detectPawnChainActions(position, lastPosition, TermAction.BETTER_WAY);
    if (betterWayCreatePawnChain) {
        negativeTerms.push(betterWayCreatePawnChain);
    }
    return negativeTerms;
}
export function positivePawnPlacements(position, lastPosition) {
    const positiveTerms = [];
    const forceDoublePawn = detectForceDoublePawn(position, lastPosition, TermAction.PERFORM);
    forceDoublePawn && positiveTerms.push(forceDoublePawn);
    const forceIsolatedPawn = detectForceIsolatedPawn(position, lastPosition, TermAction.PERFORM);
    forceIsolatedPawn && positiveTerms.push(forceIsolatedPawn);
    const offerTradeConnectPawns = detectOfferTradeConnectPawns(position, lastPosition, TermAction.PERFORM);
    offerTradeConnectPawns && positiveTerms.push(offerTradeConnectPawns);
    const offerTradePassedPawn = detectOfferTradePassedPawn(position, lastPosition, TermAction.PERFORM);
    offerTradePassedPawn && positiveTerms.push(offerTradePassedPawn);
    const passedPawn = detectPassedPawnActions(position, lastPosition, TermAction.PERFORM);
    passedPawn && positiveTerms.push(passedPawn);
    const performPromotion = detectPromotionActions(position, lastPosition, TermAction.PERFORM);
    performPromotion && positiveTerms.push(performPromotion);
    const connectPawn = detectConnectPawnActions(position, lastPosition, TermAction.PERFORM);
    connectPawn && positiveTerms.push(connectPawn);
    const createBackwardPawn = detectBackwardPawnActions(position, lastPosition, TermAction.CREATE);
    createBackwardPawn && positiveTerms.push(createBackwardPawn);
    const createPawnChain = detectPawnChainActions(position, lastPosition, TermAction.PERFORM);
    if (createPawnChain) {
        positiveTerms.push(createPawnChain);
    }
    return positiveTerms;
}
//# sourceMappingURL=pawnPlacement.js.map